<script setup lang="ts">
/**
 * A year row ‹ 2026 › over a 4 × 3 grid of month tiles — the picker behind a
 * period navigator when the user wants to jump rather than step. `v-model` is
 * `YYYY-MM`; `min` / `max` grey the tiles outside them out, and today's month
 * carries a ring.
 *
 * Keyboard on the grid: the arrows move focus, Enter or Space select (the
 * tiles are buttons, so that is the native activation), PageUp/PageDown change
 * the year and Home/End jump to January/December. The picked month is the one
 * tab stop (roving focus), so the grid costs one Tab.
 */
import { computed, ref, watch } from 'vue';
import { NxIconButton } from '../IconButton';
import { ChevronLeftIcon, ChevronRightIcon } from '@blpde/nx-icons';
import {
  formatMonth,
  isWithin,
  monthLabels,
  parseMonth,
  shiftYear,
  todayMonth,
} from './monthPicker';

const props = withDefaults(
  defineProps<{
    /** Earliest selectable month as `YYYY-MM`; earlier tiles are `aria-disabled`. */
    min?: string;
    /** Latest selectable month as `YYYY-MM`; later tiles are `aria-disabled`. */
    max?: string;
    /** BCP 47 locale for the month names. */
    locale?: string;
    /** Accessible name of the picker. */
    ariaLabel?: string;
    prevYearLabel?: string;
    nextYearLabel?: string;
  }>(),
  {
    min: undefined,
    max: undefined,
    locale: 'de-DE',
    ariaLabel: 'Monat wählen',
    prevYearLabel: 'Vorheriges Jahr',
    nextYearLabel: 'Nächstes Jahr',
  }
);

const model = defineModel<string>({ required: true });

/**
 * The month whose YEAR is on show — it follows the model, but the arrows may
 * leave it. Kept as a `YYYY-MM` value rather than a bare number so the year
 * arrows are the package's own `shiftYear()` and not a second piece of month
 * arithmetic. A malformed model falls back to the current month: the grid has to
 * show SOME year, and „NaN" is not one.
 */
function shownOf(value: string): string {
  return Number.isFinite(parseMonth(value).year) ? value : todayMonth();
}
const shown = ref(shownOf(model.value));
watch(model, (next) => (shown.value = shownOf(next)));
const year = computed(() => parseMonth(shown.value).year);

function stepYear(delta: number): void {
  shown.value = shiftYear(shown.value, delta);
}

const labels = computed(() => monthLabels(props.locale));
const current = todayMonth();
/** Roving focus stays on the picked month even while another year is on show. */
const tabStop = computed(() => parseMonth(model.value).month);

function valueOf(month: number): string {
  return formatMonth(year.value, month);
}

/** The 4 × 3 grid as ROWS of three, the way `role="grid"` requires (WAI-ARIA). */
const rows = computed(() =>
  [0, 3, 6, 9].map((start) =>
    labels.value
      .slice(start, start + 3)
      .map((label, offset) => ({ label, month: start + offset + 1 }))
  )
);

function isPickable(month: number): boolean {
  return isWithin(valueOf(month), props.min, props.max);
}

function pick(month: number): void {
  if (!isPickable(month)) return;
  model.value = valueOf(month);
}

const grid = ref<HTMLElement | null>(null);

function focusTile(month: number): void {
  grid.value?.querySelectorAll<HTMLButtonElement>('button[role="gridcell"]')[month - 1]?.focus();
}

const MOVE: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 3, ArrowUp: -3 };

function onKeydown(event: KeyboardEvent, month: number): void {
  const step = MOVE[event.key];
  if (step !== undefined) {
    event.preventDefault();
    focusTile(Math.min(12, Math.max(1, month + step)));
  } else if (event.key === 'PageDown') {
    event.preventDefault();
    stepYear(1);
  } else if (event.key === 'PageUp') {
    event.preventDefault();
    stepYear(-1);
  } else if (event.key === 'Home') {
    event.preventDefault();
    focusTile(1);
  } else if (event.key === 'End') {
    event.preventDefault();
    focusTile(12);
  }
}
</script>

<template>
  <div class="nx-month-picker" role="group" :aria-label="props.ariaLabel">
    <div class="nx-month-picker__year">
      <NxIconButton
        :aria-label="props.prevYearLabel"
        data-test="month-picker-prev-year"
        @click="stepYear(-1)"
      >
        <ChevronLeftIcon class="nxt-icon-sm" aria-hidden="true" />
      </NxIconButton>
      <span class="nx-month-picker__year-label" aria-live="polite">{{ year }}</span>
      <NxIconButton
        :aria-label="props.nextYearLabel"
        data-test="month-picker-next-year"
        @click="stepYear(1)"
      >
        <ChevronRightIcon class="nxt-icon-sm" aria-hidden="true" />
      </NxIconButton>
    </div>
    <div ref="grid" class="nx-month-picker__grid" role="grid" :aria-label="props.ariaLabel">
      <!-- `role="grid"` owns rows, and a row owns the cells: a `gridcell`
           directly under the grid is what a screen reader cannot place. The row
           is `display: contents`, so the 4 × 3 layout is still the grid's. -->
      <!-- A month outside `min`/`max` is `aria-disabled`, NOT `disabled`: the tiles
           are the same elements in every year, so PageUp/PageDown into a year
           without pickable months would turn the FOCUSED button disabled — the
           browser then drops the focus to <body>, the next key never reaches the
           grid, and the one tab stop sits on a tile Tab skips. Staying focusable
           keeps the keyboard in the grid; `pick()` refuses the month itself
           (PR #2234 review, SHOULD 2). -->
      <div v-for="row in rows" :key="row[0]?.month" class="nx-month-picker__row" role="row">
        <button
          v-for="cell in row"
          :key="cell.label"
          type="button"
          role="gridcell"
          class="nx-month-picker__tile"
          :class="{ 'is-today': valueOf(cell.month) === current }"
          :aria-selected="model === valueOf(cell.month) ? 'true' : 'false'"
          :aria-disabled="isPickable(cell.month) ? undefined : 'true'"
          :tabindex="cell.month === tabStop ? 0 : -1"
          :data-month="valueOf(cell.month)"
          @click="pick(cell.month)"
          @keydown="onKeydown($event, cell.month)"
        >
          {{ cell.label }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.nx-month-picker {
  display: grid;
  gap: var(--nxt-space-sm);
}

.nx-month-picker__year {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--nxt-space-xs);
}

.nx-month-picker__year-label {
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  font-variant-numeric: tabular-nums;
}

.nx-month-picker__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--nxt-space-2xs);
}

.nx-month-picker__row {
  display: contents;
}

.nx-month-picker__tile {
  min-height: var(--nxt-control-size-md);
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  border: var(--nxt-hairline) solid var(--nxt-border-subtle);
  border-radius: var(--nxt-radius-sm);
  background: var(--nxt-surface-bg);
  color: var(--nxt-fg);
  font: inherit;
  font-size: var(--nxt-font-size-sm);
  cursor: pointer;
  transition:
    background-color var(--nxt-motion-fast) var(--nxt-ease-out),
    border-color var(--nxt-motion-fast) var(--nxt-ease-out),
    color var(--nxt-motion-fast) var(--nxt-ease-out);
}

.nx-month-picker__tile:hover:not([aria-disabled='true'], [aria-selected='true']) {
  border-color: var(--nxt-border-strong);
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-subtle-fg);
}

.nx-month-picker__tile.is-today {
  border-color: var(--nxt-brand-border);
  font-weight: var(--nxt-font-weight-semibold);
}

.nx-month-picker__tile[aria-selected='true'] {
  border-color: var(--nxt-brand-bg);
  background: var(--nxt-brand-bg);
  color: var(--nxt-brand-fg);
}

.nx-month-picker__tile:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * 2);
}

.nx-month-picker__tile[aria-disabled='true'] {
  opacity: 0.45;
  cursor: not-allowed;
}

@media (prefers-reduced-motion: reduce) {
  .nx-month-picker__tile {
    transition: none;
  }
}
</style>
